/**
 * The attorney's positions, and the things that happen to one: added, amended,
 * confirmed, confirmed without a missing comparison, removed, an element
 * marked, and the verdict ruled.
 *
 * Every write answers the whole set, and the cache takes that answer as it
 * is: a removal renumbers the rest, so a patch of one row would leave the
 * others wrong. The served analysis is read again beside it, landed or
 * refused, since its counts and verdicts follow every write.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ElementRoleRequest,
  PositionRequest,
  PositionsResponse,
  VerdictOverrideRequest,
} from "@patent-studio/api-types";
import {
  acknowledgeGaps,
  addPosition,
  amendPosition,
  confirmPosition,
  listPositions,
  markElement,
  overrideVerdict,
  removePosition,
  suggestPositions,
} from "@/services/positions";
import {
  noveltyAnalysisKey,
  positionsKey,
  thesisGateScopes,
} from "./query-keys";
import { MUTATION_KEYS } from "@/lib/analytics/mutation-reporting";

export function usePositions(taskId: string) {
  return useQuery({
    queryKey: positionsKey(taskId),
    queryFn: () => listPositions(taskId),
  });
}

function useSetWrite<TVars>(
  taskId: string,
  mutationKey: readonly string[],
  write: (vars: TVars) => Promise<PositionsResponse>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey,
    mutationFn: write,
    onSuccess: (served) => {
      qc.setQueryData(positionsKey(taskId), served);
    },
    // Read again whether the write landed or was refused: a refusal means what
    // the screen showed has moved, and a retry against the old picture would
    // be refused the same way.
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: noveltyAnalysisKey(taskId) });
      for (const queryKey of thesisGateScopes(taskId)) {
        void qc.invalidateQueries({ queryKey });
      }
    },
  });
}

export function useAddPosition(taskId: string) {
  return useSetWrite(
    taskId,
    MUTATION_KEYS.positionAdd,
    (body: PositionRequest) => addPosition(taskId, body),
  );
}

export function useAmendPosition(taskId: string) {
  return useSetWrite(
    taskId,
    MUTATION_KEYS.positionAmend,
    ({ positionId, body }: { positionId: string; body: PositionRequest }) =>
      amendPosition(taskId, positionId, body),
  );
}

export function useConfirmPosition(taskId: string) {
  return useSetWrite(
    taskId,
    MUTATION_KEYS.positionConfirm,
    (positionId: string) => confirmPosition(taskId, positionId),
  );
}

export function useAcknowledgeGaps(taskId: string) {
  return useSetWrite(
    taskId,
    MUTATION_KEYS.positionAcknowledgeGaps,
    ({
      positionId,
      fileIds,
      reason,
    }: {
      positionId: string;
      fileIds: string[];
      reason: string;
    }) => acknowledgeGaps(taskId, positionId, fileIds, reason),
  );
}

export function useRemovePosition(taskId: string) {
  return useSetWrite(
    taskId,
    MUTATION_KEYS.positionRemove,
    (positionId: string) => removePosition(taskId, positionId),
  );
}

export interface ElementMark {
  positionId: string;
  elementId: string;
  /** The attorney's mark, or `null` to take it back to the strip's reading. */
  mark: ElementRoleRequest | null;
}

export function useMarkElement(taskId: string) {
  return useSetWrite(
    taskId,
    MUTATION_KEYS.positionMark,
    ({ positionId, elementId, mark }: ElementMark) =>
      markElement(taskId, positionId, elementId, mark),
  );
}

export interface VerdictRuling {
  positionId: string;
  /** The attorney's verdict, or `null` to leave the analysis's standing alone. */
  verdict: VerdictOverrideRequest["verdict"] | null;
  /** Why; required by the server over a Not novel verdict. */
  reason: string | null;
}

export function useOverrideVerdict(taskId: string) {
  return useSetWrite(
    taskId,
    MUTATION_KEYS.positionVerdict,
    ({ positionId, verdict, reason }: VerdictRuling) =>
      overrideVerdict(taskId, positionId, verdict, reason),
  );
}

/**
 * Ask the tool for positions. The accepted run is patched into the cached set
 * so the card reads busy before the stream's first event; the positions
 * themselves arrive through the stream, which reads the set again when the
 * run lands.
 */
export function useSuggestPositions(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: MUTATION_KEYS.positionSuggest,
    mutationFn: () => suggestPositions(taskId),
    onSuccess: async (accepted) => {
      const key = positionsKey(taskId);
      await qc.cancelQueries({ queryKey: key });
      qc.setQueryData<PositionsResponse>(key, (prev) =>
        prev ? { ...prev, suggestion: accepted } : prev,
      );
    },
  });
}
